import { useFileManager, useFileManagerUI } from "@/components/core/FileManager";
import { notifications } from "@mantine/notifications";
import { Button } from "@/components/ui/Button/Button.tsx";
import { useEffect, useMemo, useState } from "react";
import { Input, Modal, ModalActions, ModalContent, ModalTitle } from "@onengine/component-library";
import { modalBody } from "@/components/features/FileManager/Modals/modals.style.ts";
import { splitFileName, validateFileName } from "@/utils/fileNameValidation";

const MAX_FULL_NAME_LENGTH = 255;

export function RenameModal() {
  const { rename } = useFileManager();
  const { renameTarget, closeRename } = useFileManagerUI();
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { baseName, extension } = useMemo(() => {
    if (renameTarget?.type !== "FILE") return { baseName: renameTarget?.name ?? "", extension: "" };
    return splitFileName(renameTarget.name);
  }, [renameTarget]);

  useEffect(() => {
    if (!renameTarget) return;
    setValue(baseName);
    setError(null);
  }, [renameTarget?.id]);

  function handleRename() {
    if (!renameTarget) return;
    const trimmedValue = value.trimEnd();
    if (trimmedValue !== value) setValue(trimmedValue);
    const newName = `${trimmedValue}${extension}`;
    const validation = validateFileName(trimmedValue, {
      disallowExtension: extension || undefined,
    });
    if (!validation.isValid) {
      setError(validation.error ?? "Invalid name.");
      return;
    }
    if (newName.length > MAX_FULL_NAME_LENGTH) {
      setError(`Name cannot exceed ${MAX_FULL_NAME_LENGTH} characters including the extension.`);
      return;
    }
    // Server-side rename must also enforce OS filename rules and reject/re-append the extension; this client check does not replace that validation.
    rename(
      {
        id: renameTarget.id,
        currentName: renameTarget.name,
        newName,
        itemType: renameTarget.type,
      },
      {
        onSuccess: closeRename,
        onError: () => {
          notifications.show({
            title: "Rename failed",
            message: "Could not rename. Please try again.",
            color: "red",
            autoClose: 4000,
          });
        },
      },
    );
  }

  return (
    <Modal open={!!renameTarget} onClose={closeRename}>
      <ModalContent>
        <ModalTitle>Rename &#34;{renameTarget?.name}&#34;</ModalTitle>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleRename();
          }}
        >
          <div style={modalBody}>
            <Input
              label="New name"
              value={value}
              onChange={(next) => {
                setValue(next);
                setError(null);
              }}
              error={error ?? undefined}
            />
            <ModalActions position={"right"}>
              <Button type="button" variant="neutral-secondary" onClick={closeRename}>
                Cancel
              </Button>
              <Button type="submit" variant="primary">
                Rename
              </Button>
            </ModalActions>
          </div>
        </form>
      </ModalContent>
    </Modal>
  );
}
